import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiPencil, HiDocumentText } from "react-icons/hi";
import { DataTable, PageLayout, ActionButton } from "../../../components/modern";
import type { Column, DateRangeValue } from "../../../components/modern";
import { hasPermission } from "../../../utils/permissions";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toastNotifications";
import './InvoiceIndex.scss';

interface Invoice {
  id: number;
  orderId?: number;
  shipForMeId?: number;
  orderNumber?: string;
  shipForMeNumber?: string;
  customerName: string;
  amountIQD: number;
  receivedInvoiceNumber?: string;
  receivedDate?: string;
  externalInvoiceNumber?: string;
  collectionDate?: string;
  isCollected: boolean;
  isReceived: boolean;
  invoiceNumber?: string; // Invoice Number in format: INV-S-{id} or INV-B-{id}
  paymentGatewayId?: number;
  paymentGatewayName?: string;
}

export const InvoiceIndex: React.FC = () => {
  const { t, i18n } = useTranslation(['invoices', 'common']);
  const navigate = useNavigate();
  const { token } = useAuth();
  const isRTL = i18n.language === 'ar';

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [dateFilters, setDateFilters] = useState<Record<string, DateRangeValue>>({});

  const buildQuery = (page: number, size: number) => {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: size.toString()
    });

    const numericKeys = new Set(['amountIQD']);
    const booleanKeys = new Set(['isCollected', 'isReceived']);

    const parseBoolean = (key: string, rawValue: string) => {
      const value = rawValue.trim().toLowerCase();
      if (!value) return null;

      if (key === 'isCollected') {
        if (['true', '1', 'yes', 'collected'].includes(value)) return true;
        if (['false', '0', 'no', 'not collected', 'notcollected'].includes(value)) return false;
      }

      if (key === 'isReceived') {
        if (['true', '1', 'yes', 'received'].includes(value)) return true;
        if (['false', '0', 'no', 'not received', 'notreceived'].includes(value)) return false;
      }

      return null;
    };

    Object.entries(filters).forEach(([key, value]) => {
      const trimmed = value.trim();
      if (!trimmed) return;

      if (numericKeys.has(key)) {
        const parsed = Number(trimmed.replace(/[^0-9.]/g, ''));
        if (Number.isNaN(parsed)) return;
        params.append(key, parsed.toString());
        return;
      }

      if (booleanKeys.has(key)) {
        const parsed = parseBoolean(key, trimmed);
        if (parsed === null) return;
        params.append(key, parsed ? 'true' : 'false');
        return;
      }

      params.append(key, trimmed);
    });

    Object.entries(dateFilters).forEach(([key, range]) => {
      if (range.from) params.append(`${key}From`, range.from);
      if (range.to) params.append(`${key}To`, range.to);
    });

    return params.toString();
  };

  const fetchInvoices = async (page: number, size: number) => {
    try {
      setLoading(true);
      const query = buildQuery(page, size);
      const response = await adminApi.call(`/invoice?${query}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || t('invoices:messages.fetchError'));
        return;
      }

      setInvoices(response.data?.items || []);
      setTotalItems(response.data?.totalItems || 0);
    } catch (error: any) {
      showErrorToast(t('invoices:messages.fetchError'));
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  // Build export query with filters - map to backend ExportFilters parameter names
  const buildExportQuery = () => {
    const params = new URLSearchParams();

    const appendText = (key: string, raw?: string) => {
      const trimmed = raw?.trim();
      if (!trimmed) return;
      params.append(key, trimmed);
    };

    const appendFloat = (key: string, raw?: string) => {
      const trimmed = raw?.trim();
      if (!trimmed) return;
      const normalized = trimmed.replace(/[^0-9.]/g, '');
      if (!normalized) return;
      const parsed = Number(normalized);
      if (Number.isNaN(parsed)) return;
      params.append(key, parsed.toString());
    };

    appendText('InvoiceNumber', filters.invoiceNumber);
    appendText('OrderNumber', filters.orderNumber);
    appendText('CustomerName', filters.customerName);
    appendFloat('AmountIQD', filters.amountIQD);
    appendText('ReceivedInvoiceNumber', filters.receivedInvoiceNumber);
    appendText('ExternalInvoiceNumber', filters.externalInvoiceNumber);

    // Map invoice filter booleans to backend ExportFilters property names
    if (filters.isCollected !== undefined && filters.isCollected !== '') {
      const isCollectedValue = filters.isCollected.trim().toLowerCase();
      if (['true', '1', 'yes', 'collected'].includes(isCollectedValue)) {
        params.append('IsCollected', 'true');
      } else if (['false', '0', 'no', 'not collected', 'notcollected'].includes(isCollectedValue)) {
        params.append('IsCollected', 'false');
      }
    }

    if (filters.isReceived !== undefined && filters.isReceived !== '') {
      const isReceivedValue = filters.isReceived.trim().toLowerCase();
      if (['true', '1', 'yes', 'received'].includes(isReceivedValue)) {
        params.append('IsReceived', 'true');
      } else if (['false', '0', 'no', 'not received', 'notreceived'].includes(isReceivedValue)) {
        params.append('IsReceived', 'false');
      }
    }

    // Date filters
    if (dateFilters.receivedDate?.from) {
      params.append('ReceivedDateFrom', dateFilters.receivedDate.from);
    }
    if (dateFilters.receivedDate?.to) {
      params.append('ReceivedDateTo', dateFilters.receivedDate.to);
    }
    if (dateFilters.collectionDate?.from) {
      params.append('CollectionDateFrom', dateFilters.collectionDate.from);
    }
    if (dateFilters.collectionDate?.to) {
      params.append('CollectionDateTo', dateFilters.collectionDate.to);
    }

    return params.toString();
  };

  // Export invoices to Excel
  const handleExport = async () => {
    try {
      const query = buildExportQuery();
      const url = query ? `${import.meta.env.VITE_APP_API_BASE_URL}/invoice/export?${query}` : `${import.meta.env.VITE_APP_API_BASE_URL}/invoice/export`;
      
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export invoices');
      }

      const blob = await response.blob();
      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj;
      a.download = `Invoices_Export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(urlObj);
      document.body.removeChild(a);
      showSuccessToast('Invoices exported successfully');
    } catch (error) {
      console.error('Error exporting invoices:', error);
      showErrorToast('Failed to export invoices. Please try again.');
    }
  };

  useEffect(() => {
    fetchInvoices(currentPage, pageSize);
  }, [currentPage, pageSize, filters, dateFilters, i18n.language]);

  const handleCheckboxChange = (id: number) => {
    setSelectedIds((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((selectedId) => selectedId !== id)
        : [...prevSelected, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === invoices.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(invoices.map(invoice => invoice.id));
    }
  };

  const handleBulkAction = () => {
    if (selectedIds.length === 0) {
      showErrorToast('Please select at least one invoice.');
      return;
    }
    setShowModal(true);
  };

  const submitBulkAction = async () => {
    if (!invoiceNumber.trim()) {
      showErrorToast('Please enter an invoice number.');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_APP_API_BASE_URL}/invoice/bulk-update`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            InvoiceIds: selectedIds,
            receivedInvoiceNumber: invoiceNumber.trim(),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update invoices");
      }

      showSuccessToast('Invoices updated successfully!');
      setSelectedIds([]);
      setInvoiceNumber("");
      setShowModal(false);
      fetchInvoices(currentPage, pageSize);
    } catch (error) {
      showErrorToast('Error: ' + error.message);
      console.error('Error updating invoices:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setInvoiceNumber("");
  };

  const deleteInvoice = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_APP_API_BASE_URL}/invoice/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete invoice");
      }

      showSuccessToast('Invoice deleted successfully.');
      fetchInvoices(currentPage, pageSize);
    } catch (error) {
      showErrorToast('Error: ' + error.message);
      console.error('Error deleting invoice:', error);
    }
  };

  const handleCreateInvoice = () => {
    navigate('/invoices/create');
  };

  const handleEditInvoice = (id: number) => {
    navigate(`/invoices/edit/${id}`);
  };

  const handleViewInvoice = (id: number) => {
    navigate(`/invoices/${id}`);
  };

  const columns: Column[] = [
    {
      key: 'select',
      title: t('common:select'),
      sortable: false,
      width: '50px',
      render: (_, invoice) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(invoice.id)}
          onChange={() => handleCheckboxChange(invoice.id)}
        />
      ),
    },
    {
      key: 'invoiceNumber',
      title: t('invoices:fields.invoiceNumber', 'Invoice Number'),
      sortable: true,
      filterable: true,
      filterKey: 'invoiceNumber',
      render: (value, invoice) => value || invoice.orderNumber || invoice.shipForMeNumber || invoice.id,
    },
    {
      key: 'no',
      title: t('invoices:fields.orderNumber', 'Order No'),
      sortable: true,
      filterable: true,
      filterKey: 'orderNumber',
      filterValue: (row) => {
        return row.orderNumber || row.shipForMeNumber || '-';
      },
      render: (_, invoice) => {
        return invoice.orderNumber || invoice.shipForMeNumber || '-';
      },
    },
    {
      key: 'customerName',
      title: t('invoices:table.customer'),
      sortable: true,
      filterable: true,
      filterKey: 'customerName',
      render: (value) => value || '-',
    },
    {
      key: 'paymentGatewayName',
      title: t('invoices:fields.paymentGateway', 'Payment Gateway'),
      sortable: true,
      filterable: true,
      filterKey: 'paymentGatewayName',
      render: (value) => value || '-',
    },
    {
      key: 'amountIQD',
      title: t('invoices:fields.amount') + '(' + t('invoices:fields.iqd')+ ')',
      sortable: true,
      filterable: true,
      filterKey: 'amountIQD',
      width: '120px',
      render: (value) => (
        <div className="amount-display iqd">
          <span className="amount-value">
            {value ? (
              Math.ceil(Number(value) / 1000) * 1000
            )
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "0"}
          </span>
        </div>
      )
    },
    {
      key: 'receivedInvoiceNumber',
      title: t('invoices:fields.receivedInvoiceNumber'),
      sortable: true,
      filterable: true,
      filterKey: 'receivedInvoiceNumber',
      render: (value) => value || '-',
    },
    {
      key: 'receivedDate',
      title: t('invoices:fields.receivedDate'),
      sortable: true,
      filterable: true,
      filterKey: 'receivedDate',
      filterType: 'dateRange',
      filterValue: (row) => row.receivedDate ? new Date(row.receivedDate).toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }) : '-',
      render: (value) => value ? new Date(value).toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }) : '-',
    },
    {
      key: 'externalInvoiceNumber',
      title: t('invoices:fields.externalInvoiceNumber'),
      sortable: true,
      filterable: true,
      filterKey: 'externalInvoiceNumber',
      render: (value) => value || '-',
    },
    {
      key: 'collectionDate',
      title: t('invoices:fields.collectionDate'),
      sortable: true,
      filterable: true,
      filterKey: 'collectionDate',
      filterType: 'dateRange',
      filterValue: (row) => row.collectionDate ? new Date(row.collectionDate).toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }) : '-',
      render: (value) => value ? new Date(value).toLocaleString("en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }) : '-',
    },
    {
      key: 'isCollected',
      title: t('invoices:fields.isCollected'),
      sortable: true,
      filterable: true,
      filterKey: 'isCollected',
      filterValue: (row) => row.isCollected ? t('common:status.collected', 'Collected') : t('common:status.notCollected', 'Not Collected'),
      render: (value) => (
        <span className={`badge bg-${value ? "success" : "danger"}`}>
          {value ? t('common:status.collected', 'Collected') : t('common:status.notCollected', 'Not Collected')}
        </span>
      ),
    },
    {
      key: 'isReceived',
      title: t('invoices:fields.isReceived'),
      sortable: true,
      filterable: true,
      filterKey: 'isReceived',
      filterValue: (row) => row.isReceived ? t('common:status.received', 'Received') : t('common:status.notReceived', 'Not Received'),
      render: (value) => (
        <span className={`badge bg-${value ? "success" : "danger"}`}>
          {value ? t('common:status.received', 'Received') : t('common:status.notReceived', 'Not Received')}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      sortable: false,
      width: '50px',
      align: 'center',
      render: (_, invoice) => (
        <div className="action-buttons">
          {invoice.orderId && hasPermission("View Order") && (
            <ActionButton
              variant="info"
              size="sm"
              icon={HiDocumentText}
              onClick={() => window.open(`/order/printinvoice/${invoice.id}`, '_blank')}
              tooltip={t("invoices:PrintInvoice")}
            />
          )}
          {invoice.shipForMeId && hasPermission("View Ship For Me") && (
            <ActionButton
              variant="warning"
              size="sm"
              icon={HiDocumentText}
              onClick={() => window.open(`/shipforme/printinvoice/${invoice.id}`, '_blank')}
              tooltip={t("invoices:PrintInvoice")}
            />
          )}
          {hasPermission("Update Invoice") && (
            <ActionButton
              variant="primary"
              size="sm"
              icon={HiPencil}
              onClick={() => handleEditInvoice(invoice.id)}
              tooltip={t("invoices:editInvoice")}
            />
          )}
        </div>
      ),
    },
  ];

  const breadcrumbs = [
    { label: t('invoices:breadcrumbs.invoices') }
  ];

  const headerActions = (
    <div className="header-actions">
      {selectedIds.length > 0 && hasPermission("Update Invoice") && (
        <ActionButton
          variant="primary"
          onClick={handleBulkAction}
          className="me-2"
        >
          <i className="fa fa-edit me-1"></i>
          {t('invoices:updateSelected')} ({selectedIds.length})
        </ActionButton>
      )}
      <ActionButton
        variant="outline-secondary"
        size="sm"
        onClick={handleSelectAll}
      >
        {selectedIds.length === invoices.length && invoices.length > 0
          ? t('common:buttons.deselectAll', 'Deselect All')
          : t('common:buttons.selectAll', 'Select All')}
      </ActionButton>
    </div>
  );

  return (
    <PageLayout
      title={t('invoices:title')}
      subtitle={t('invoices:subtitle')}
      breadcrumbs={breadcrumbs}
      headerActions={headerActions}
      className={isRTL ? 'rtl' : 'ltr'}
      isIndexPage={true}
    >
      <DataTable
        columns={columns}
        data={invoices}
        loading={loading}
        searchable={true}
        filterable={true}
        exportable={true}
        onExport={handleExport}
        refreshable={true}
        paginationMode="server"
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 50]}
        showPageSizeSelector={true}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        onFilterChange={(key, value) => {
          setFilters(prev => ({ ...prev, [key]: value }));
          setCurrentPage(1);
        }}
        onDateRangeChange={(key, range) => {
          setDateFilters(prev => ({ ...prev, [key]: range }));
          setCurrentPage(1);
        }}
        onRefresh={() => fetchInvoices(currentPage, pageSize)}
        emptyMessage={t('invoices:messages.noInvoices')}
      />

      {/* Modal for Bulk Update */}
      {showModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-dark">Receive Invoices</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-dark">
                  {t('invoices:selectedForUpdate', { count: selectedIds.length })}
                </p>
                <div className="mb-3">
                  <label className="form-label text-dark">{t('invoices:fields.invoiceNumber')}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder={t('invoices:placeholders.invoiceNumber')}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <ActionButton
                  variant="secondary"
                  onClick={closeModal}
                >
                  {t('common:buttons.cancel')}
                </ActionButton>
                <ActionButton
                  variant="primary"
                  onClick={submitBulkAction}
                  loading={submitting}
                  disabled={submitting}
                >
                  {submitting ? t('invoices:buttons.updating') : t('invoices:buttons.update')}
                </ActionButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};
